import { useContext, useEffect, useMemo, type FC } from "react";

import clsx from "clsx";
import { StringParam, useQueryParams, withDefault } from "use-query-params";

import { gql } from "@apollo/client";
import { useUser } from "@components/user-context";
import { useFeatureFlags, useNorthbeamMutation } from "@hooks/http";
import { type RefreshAMIV2s } from "@north-beam/nb-client-side";
import { PrimaryButton } from "@shared/buttons";
import Spinner from "@shared/spinner";

import { SideNavContext } from "..";
import { useOnboardingStep } from "../onboarding-nav/use-onboarding-number";
import { Steps } from "../steps";

import { Step, type StepChildProps } from "./step";
import { StepContainer } from "./step-container";
import { AttributionValidation } from "./verify/attribution-validation";
import { PixelValidation } from "./verify/pixel-validation";

export const Verify: FC<StepChildProps> = ({
  nextStep,
  goToNamedStep,
  isMarkedComplete,
  toggleStepComplete,
  ...props
}) => {
  const { setSubNavItems } = useContext(SideNavContext);
  const { user } = useUser();
  const { hasFeatureFlag } = useFeatureFlags();

  const [refreshAMIs, { loading: loadingAMIData, data: AMIData }] =
    useNorthbeamMutation<RefreshAMIV2s>(REFRESH_AMIS, {
      useHeavy: true,
      fetchPolicy: "no-cache",
    });

  useEffect(() => {
    refreshAMIs();
  }, [refreshAMIs]);

  const [_, setSearchParams] = useQueryParams({
    subStep: withDefault(StringParam, ""),
    step: withDefault(StringParam, ""),
    provider: withDefault(StringParam, ""),
  });

  const finishEnabled = useMemo(() => {
    if (user.isPixelConfigurationSubaccount) {
      return AMIData?.refreshOrdersSyncAMI;
    }

    if (!props.stepMetadata?.isDataVerified) {
      return false;
    }

    return (
      props.unblockedFinishOnVerifyScreen &&
      AMIData?.refreshConversionPixelAMI &&
      AMIData?.refreshOrdersSyncAMI &&
      AMIData?.refreshTestUrlsForNBPixelSetupAMI &&
      AMIData?.refreshDNSAMI
    );
  }, [
    AMIData?.refreshConversionPixelAMI,
    AMIData?.refreshDNSAMI,
    AMIData?.refreshOrdersSyncAMI,
    AMIData?.refreshTestUrlsForNBPixelSetupAMI,
    props.unblockedFinishOnVerifyScreen,
    props.stepMetadata?.isDataVerified,
    user.isPixelConfigurationSubaccount,
  ]);

  useEffect(() => {
    setSubNavItems([]);
  }, [setSubNavItems]);

  const { stepDictionary } = useOnboardingStep();
  const stepIndex = stepDictionary["verify"].index;

  return (
    <StepContainer
      hideFooter
      knowledgeBaseLink=""
      knowledgeBaseText=""
      title={`${stepIndex}. Verify Your Setup`}
      nextStepText="FINISH"
      moveForwardButtonDisabled={!finishEnabled}
      onMoveBackwardButtonClicked={() => {
        setSearchParams({
          step: Steps.SPEND_API,
        });
        props.previousStep?.();
      }}
      onMoveForwardButtonClicked={() => {
        if (!isMarkedComplete) {
          toggleStepComplete();
        }
        setSearchParams({
          step: Steps.COMPLETE,
        });
        nextStep?.();
      }}
    >
      <Step
        className="mt-6"
        nextDisabled={!finishEnabled}
        nextStepButtonText="Finish"
        nextStep={() => {
          if (!isMarkedComplete) {
            toggleStepComplete();
          }
          setSearchParams({
            step: Steps.COMPLETE,
          });
          nextStep?.();
        }}
      >
        <div className="h-full overflow-hidden p-10">
          <div className="flex flex-col">
            <div className="flex  flex-row items-center gap-5">
              <div className="text-2xl font-semibold">
                All checks must pass to complete Onboarding
              </div>
              <div>
                <PrimaryButton
                  className="flex font-semibold gap-2 items-center justify-center"
                  onClick={async () => {
                    await refreshAMIs();
                  }}
                >
                  <i className="fa-solid fa-rotate-right"></i>
                  <div className="font-semibold">Retry</div>
                </PrimaryButton>
              </div>
            </div>
            <div className="flex justify-center items-center flex-col">
              {!user.isPixelConfigurationSubaccount && (
                <>
                  <div
                    className={clsx(
                      "flex flex-col gap-8 min-w-[800px] mt-8 rounded",
                      {
                        "bg-background": loadingAMIData,
                        "bg-alien-2": AMIData?.refreshDNSAMI && !loadingAMIData,

                        "bg-mars-2": !AMIData?.refreshDNSAMI && !loadingAMIData,
                      },
                    )}
                  >
                    <div className="w-full rounded border-gray-0 border p-4 flex items-center gap-2 justify-between">
                      <div className="flex-center gap-2">
                        {!loadingAMIData && !AMIData?.refreshDNSAMI && (
                          <i className="fa-regular fa-circle-xmark text-danger text-lg"></i>
                        )}
                        {loadingAMIData && <Spinner />}
                        {!loadingAMIData && AMIData?.refreshDNSAMI && (
                          <input
                            id="completedStep"
                            type="checkbox"
                            className={clsx("pb-0 mb-0", {
                              "checked:before:border-alien+0 checked:before:bg-alien+0":
                                true,
                            })}
                            checked={true}
                          />
                        )}
                        <div className="text-lg font-semibold">DNS Setup</div>
                      </div>
                      <button
                        className="text-cosmic font-semibold hover:underline hover:underline-offset-2"
                        onClick={() => {
                          goToNamedStep && goToNamedStep("dns-setup");
                          setSearchParams({
                            step: Steps.DNS_SETUP,
                            subStep: "",
                            provider: "",
                          });
                        }}
                      >
                        Go to step
                      </button>
                    </div>
                  </div>
                  <div
                    className={clsx(
                      "flex flex-col gap-8 min-w-[800px] mt-8 rounded",
                      {
                        "bg-background": loadingAMIData,
                        "bg-alien-2":
                          AMIData?.refreshConversionPixelAMI &&
                          AMIData?.refreshTestUrlsForNBPixelSetupAMI &&
                          !loadingAMIData,

                        "bg-mars-2":
                          (!AMIData?.refreshConversionPixelAMI ||
                            !AMIData?.refreshTestUrlsForNBPixelSetupAMI) &&
                          !loadingAMIData,
                      },
                    )}
                  >
                    <div className="w-full rounded border-gray-0 border p-4 flex items-center gap-2 justify-between">
                      <div className="flex-center gap-2">
                        {!loadingAMIData &&
                          (!AMIData?.refreshConversionPixelAMI ||
                            !AMIData?.refreshTestUrlsForNBPixelSetupAMI) && (
                            <i className="fa-regular fa-circle-xmark text-danger text-lg"></i>
                          )}
                        {loadingAMIData && <Spinner />}
                        {!loadingAMIData &&
                          AMIData?.refreshConversionPixelAMI &&
                          AMIData?.refreshTestUrlsForNBPixelSetupAMI && (
                            <input
                              id="completedStep"
                              type="checkbox"
                              className={clsx("pb-0 mb-0", {
                                "checked:before:border-alien+0 checked:before:bg-alien+0":
                                  true,
                              })}
                              checked={true}
                            />
                          )}
                        <div className="text-lg font-semibold">Pixel Setup</div>
                      </div>
                      <button
                        className="text-cosmic font-semibold hover:underline hover:underline-offset-2"
                        onClick={() => {
                          goToNamedStep && goToNamedStep("pixel-placement");
                          setSearchParams({
                            step: Steps.PIXEL_PLACEMENT,
                            subStep: "",
                            provider: "",
                          });
                        }}
                      >
                        Go to step
                      </button>
                    </div>
                  </div>
                </>
              )}
              <div
                className={clsx(
                  "flex flex-col gap-8 min-w-[800px] mt-8 rounded",
                  {
                    "bg-background": loadingAMIData,
                    "bg-alien-2":
                      AMIData?.refreshOrdersSyncAMI && !loadingAMIData,

                    "bg-mars-2":
                      !AMIData?.refreshOrdersSyncAMI && !loadingAMIData,
                  },
                )}
              >
                <div className="w-full rounded border-gray-0 border p-4 flex items-center gap-2 justify-between">
                  <div className="flex-center gap-2">
                    {!loadingAMIData && !AMIData?.refreshOrdersSyncAMI && (
                      <i className="fa-regular fa-circle-xmark text-danger text-lg"></i>
                    )}
                    {loadingAMIData && <Spinner />}
                    {!loadingAMIData && AMIData?.refreshOrdersSyncAMI && (
                      <input
                        id="completedStep"
                        type="checkbox"
                        className={clsx("pb-0 mb-0", {
                          "checked:before:border-alien+0 checked:before:bg-alien+0":
                            true,
                        })}
                        checked={true}
                      />
                    )}
                    <div className="text-lg font-semibold">
                      Orders Connected
                    </div>
                  </div>
                  <button
                    className="text-cosmic font-semibold hover:underline hover:underline-offset-2"
                    onClick={() => {
                      goToNamedStep && goToNamedStep("orders-api");
                      setSearchParams({
                        step: Steps.ORDERS_API,
                        subStep: "",
                        provider: "",
                      });
                    }}
                  >
                    Go to step
                  </button>
                </div>
              </div>
            </div>
            <AttributionValidation
              stepMetadata={props.stepMetadata ?? {}}
              updateStepMetadata={props.updateStepMetadata}
            />
            {hasFeatureFlag("enablePixelValidationInOnboardingFlow") && (
              <PixelValidation
                stepMetadata={props.stepMetadata ?? {}}
                updateStepMetadata={props.updateStepMetadata}
              />
            )}
          </div>
        </div>
      </Step>
    </StepContainer>
  );
};

const REFRESH_AMIS = gql`
  mutation RefreshAMIV2s {
    refreshDNSAMI
    refreshConversionPixelAMI
    refreshTestUrlsForNBPixelSetupAMI
    refreshOrdersSyncAMI
  }
`;
